import React, { useState, useEffect, useCallback } from 'react';
import { X, Search, Eye, QrCode, Shield, Copy, Check, Image as ImageIcon, CheckCircle2, Printer } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { fetchCorrespondenciasPorCita, Correspondencia } from '../../../../services/correspondencia';
import { useTheme } from '../../../../context/ThemeContext';


interface HistorialCorrespondenciaUsuariosProps {
    isOpen: boolean;
    onClose: () => void;
    citaId: number;
    citaCodigo: string;
}

const HistorialCorrespondenciaUsuarios: React.FC<HistorialCorrespondenciaUsuariosProps> = ({
    isOpen,
    onClose,
    citaId,
    citaCodigo
}) => {
    const { isDark } = useTheme();
    const [correspondencias, setCorrespondencias] = useState<Correspondencia[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [busqueda, setBusqueda] = useState('');
    const [filtroEstado, setFiltroEstado] = useState<string>('todos');
    const [verFotoModal, setVerFotoModal] = useState<string | null>(null);
    const [comprobanteQR, setComprobanteQR] = useState<Correspondencia | null>(null);
    const [copiadoPinId, setCopiadoPinId] = useState<number | null>(null);
    const [copiadoCodigoId, setCopiadoCodigoId] = useState<number | null>(null);

    const loadCorrespondencias = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await fetchCorrespondenciasPorCita(citaId);
            setCorrespondencias(data);
        } catch (error) {
            /* Browser diagnostics omitted. */
        } finally {
            setIsLoading(false);
        }
    }, [citaId]);

    useEffect(() => {
        if (isOpen && citaId) {
            loadCorrespondencias();
        }
    }, [isOpen, citaId, loadCorrespondencias]);

    const formatDateTime = (dateString: string | null): string => {
        if (!dateString) return 'Pendiente';

        const date = new Date(dateString);
        return date.toLocaleString('es-BO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    };

    const getEstadoColor = (estado: string) => {
        switch (estado) {
            case 'recibido':
                return isDark ? 'bg-purple-950/60 text-purple-300 border-purple-800/60' : 'bg-purple-100 text-purple-800 border-purple-200';
            case 'notificado':
                return isDark ? 'bg-amber-950/60 text-amber-300 border-amber-800/60' : 'bg-amber-100 text-amber-800 border-amber-200';
            case 'entregado':
                return isDark ? 'bg-emerald-950/60 text-emerald-300 border-emerald-800/60' : 'bg-emerald-100 text-emerald-800 border-emerald-200';
            case 'cancelado':
                return isDark ? 'bg-rose-950/60 text-rose-300 border-rose-800/60' : 'bg-rose-100 text-rose-800 border-rose-200';
            default:
                return isDark ? 'bg-gray-800 text-gray-300 border-gray-700' : 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getEstadoTexto = (estado: string) => {
        switch (estado) {
            case 'recibido':
                return 'En Recepción';
            case 'notificado':
                return 'Listo para Retiro';
            case 'entregado':
                return 'Entregado';
            case 'cancelado':
                return 'Cancelado';
            default:
                return estado;
        }
    };

    const handleCopiarPin = (id: number, pin: string) => {
        navigator.clipboard.writeText(pin);
        setCopiadoPinId(id);
        setTimeout(() => setCopiadoPinId(null), 2000);
    };

    const handleCopiarCodigo = (id: number, codigo: string) => {
        navigator.clipboard.writeText(codigo);
        setCopiadoCodigoId(id);
        setTimeout(() => setCopiadoCodigoId(null), 2000);
    };

    const handleImprimirComprobante = () => {
        window.print();
    };

    const correspondenciasFiltradas = correspondencias.filter(correspondencia => {
        if (filtroEstado !== 'todos' && correspondencia.estado !== filtroEstado) {
            return false;
        }

        if (busqueda.trim()) {
            const searchTerm = busqueda.toLowerCase();
            return (
                (correspondencia.codigo && correspondencia.codigo.toLowerCase().includes(searchTerm)) ||
                (correspondencia.descripcion && correspondencia.descripcion.toLowerCase().includes(searchTerm)) ||
                correspondencia.remitente_nombre.toLowerCase().includes(searchTerm) ||
                correspondencia.remitente_apellido.toLowerCase().includes(searchTerm) ||
                correspondencia.remitente_ci_nit.toLowerCase().includes(searchTerm)
            );
        }

        return true;
    });

    if (!isOpen) return null;

    return (
        <>
            <style>{`
                .historial-scroll::-webkit-scrollbar {
                    width: 6px;
                }
                .historial-scroll::-webkit-scrollbar-track {
                    background: transparent;
                }
                .historial-scroll::-webkit-scrollbar-thumb {
                    background-color: rgba(97, 37, 123, 0.3);
                    border-radius: 20px;
                }
                .historial-scroll::-webkit-scrollbar-thumb:hover {
                    background-color: rgba(97, 37, 123, 0.5);
                }
                .historial-scroll {
                    scrollbar-width: thin;
                    scrollbar-color: rgba(97, 37, 123, 0.3) transparent;
                }
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    #print-comprobante-qr, #print-comprobante-qr * {
                        visibility: visible;
                    }
                    #print-comprobante-qr {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }
                }
            `}</style>

            {/* Modal de visualización de foto a tamaño completo */}
            {verFotoModal && (
                <div
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-[80] p-4 cursor-pointer backdrop-blur-sm"
                    onClick={() => setVerFotoModal(null)}
                >
                    <div 
                        className="relative max-w-3xl max-h-[85vh] rounded-2xl overflow-hidden shadow-2xl p-2"
                        style={{ background: isDark ? '#150a26' : '#ffffff', border: isDark ? '1px solid rgba(255,255,255,0.1)' : 'none' }}
                        onClick={e => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setVerFotoModal(null)}
                            className="absolute top-4 right-4 p-2 bg-black/60 text-white rounded-full hover:bg-black transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <img src={verFotoModal} alt="Foto Ampliada" className="max-w-full max-h-[80vh] object-contain rounded-xl" />
                    </div>
                </div>
            )}

            {/* Modal de Comprobante QR para Retiro */}
            {comprobanteQR && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[75] p-4 backdrop-blur-sm">
                    <div 
                        className="rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-up" 
                        style={{ background: isDark ? '#150a26' : '#ffffff', border: isDark ? '1.5px solid rgba(193, 52, 139, 0.3)' : '1px solid #e5e7eb' }}
                        id="print-comprobante-qr"
                    >
                        {/* Header del comprobante */}
                        <div className="bg-gradient-to-r from-[#61257B] to-[#C1348B] p-6 text-white text-center relative">
                            <button
                                onClick={() => setComprobanteQR(null)}
                                className="absolute top-4 right-4 p-1.5 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                            <Shield className="w-10 h-10 mx-auto mb-2 text-white/90" />
                            <h3 className="text-xl font-bold">Comprobante de Retiro</h3>
                            <p className="text-xs text-white/80 mt-0.5">Presenta este comprobante en recepción</p>
                        </div>
                        {/* Cuerpo del comprobante */}
                        <div className="p-6 space-y-4 text-center">
                            <div className={`p-3 rounded-2xl text-xs font-medium text-left ${isDark ? 'bg-emerald-950/40 border border-emerald-800/50 text-emerald-300' : 'bg-emerald-50 border border-emerald-200 text-emerald-800'}`}>
                                💡 <strong>3 Opciones para recoger:</strong> Muestra el <strong>QR</strong> a la cámara, o proporciona tu <strong>Código</strong> o <strong>PIN</strong> al recepcionista.
                            </div>

                            {/* Opción 1: Código de Retiro */}
                            <div 
                                className="rounded-2xl p-4 text-left flex items-center justify-between"
                                style={{
                                    background: isDark ? 'rgba(97, 37, 123, 0.15)' : 'rgba(97, 37, 123, 0.05)',
                                    border: isDark ? '1px solid rgba(193, 52, 139, 0.3)' : '1px solid rgba(97, 37, 123, 0.2)'
                                }}
                            >
                                <div>
                                    <span className="text-[11px] font-bold uppercase tracking-wider block" style={{ color: isDark ? '#ffd97c' : '#61257B' }}>
                                        Código de Retiro
                                    </span>
                                    <p className="text-xl font-mono font-black tracking-wider" style={{ color: isDark ? '#ffffff' : '#111827' }}>
                                        {comprobanteQR.codigo || `#${comprobanteQR.id}`}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleCopiarCodigo(comprobanteQR.id, comprobanteQR.codigo || `#${comprobanteQR.id}`)}
                                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-xl shadow-sm transition-all"
                                    style={{
                                        background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#ffffff',
                                        color: isDark ? '#ffffff' : '#61257B',
                                        border: isDark ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(97, 37, 123, 0.25)'
                                    }}
                                >
                                    {copiadoCodigoId === comprobanteQR.id ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                                    <span>{copiadoCodigoId === comprobanteQR.id ? '¡Copiado!' : 'Copiar Código'}</span>
                                </button>
                            </div>

                            {/* Opción 2: PIN de Seguridad */}
                            {comprobanteQR.pin_seguridad && (
                                <div className={`rounded-2xl p-4 text-left flex items-center justify-between ${isDark ? 'bg-amber-950/40 border border-amber-800/50' : 'bg-amber-50/80 border border-amber-200'}`}>
                                    <div>
                                        <span className={`text-[11px] font-bold uppercase tracking-wider block ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>PIN Secreto de Retiro</span>
                                        <p className={`text-2xl font-mono font-black tracking-widest ${isDark ? 'text-amber-200' : 'text-amber-900'}`}>
                                            {comprobanteQR.pin_seguridad}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleCopiarPin(comprobanteQR.id, comprobanteQR.pin_seguridad || '')}
                                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-xl shadow-sm transition-all"
                                        style={{
                                            background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#ffffff',
                                            color: isDark ? '#ffd97c' : '#b45309',
                                            border: isDark ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid #fde68a'
                                        }}
                                    >
                                        {copiadoPinId === comprobanteQR.id ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                                        <span>{copiadoPinId === comprobanteQR.id ? '¡Copiado!' : 'Copiar PIN'}</span>
                                    </button>
                                </div>
                            )}

                            {/* Opción 3: Código QR */}
                            <div className="bg-white p-4 rounded-2xl shadow-inner border border-gray-200 inline-block">
                                <QRCodeSVG 
                                    value={JSON.stringify({
                                        id: comprobanteQR.id,
                                        codigo: comprobanteQR.codigo,
                                        pin: comprobanteQR.pin_seguridad,
                                        tipo: 'retiro_correspondencia'
                                    })}
                                    size={180}
                                    level="H"
                                    includeMargin={true}
                                />
                            </div>

                            <p className="text-xs" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>
                                Escanea este QR directamente en el punto de recepción de Co Marca.
                            </p>

                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={handleImprimirComprobante}
                                    className="flex-1 py-2.5 px-4 text-xs font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
                                    style={{
                                        background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6',
                                        color: isDark ? '#ffffff' : '#374151',
                                        border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : 'none'
                                    }}
                                >
                                    <Printer className="w-4 h-4" />
                                    Imprimir
                                </button>
                                <button
                                    onClick={() => setComprobanteQR(null)}
                                    className="flex-1 py-2.5 px-4 text-xs font-semibold text-white bg-gradient-to-r from-[#61257B] to-[#C1348B] hover:opacity-95 shadow-md rounded-xl transition-all"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                <div 
                    className="rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto historial-scroll"
                    style={{
                        background: isDark ? 'rgba(22, 10, 43, 0.95)' : '#ffffff',
                        border: isDark ? '1.5px solid rgba(193, 52, 139, 0.28)' : '1px solid #e5e7eb',
                        backdropFilter: 'blur(16px)'
                    }}
                >
                    <div className="p-6 md:p-8">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-2xl font-bold" style={{ color: isDark ? '#ffffff' : '#1f2937' }}>
                                    Mi Correspondencia y Paquetes
                                </h2>
                                <p className="mt-1 text-sm" style={{ color: isDark ? '#cbd5e1' : '#4b5563' }}>
                                    Reserva <span className="font-semibold" style={{ color: isDark ? '#ffd97c' : '#61257B' }}>{citaCodigo}</span> • {correspondencias.length} paquete(s) registrados
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-lg transition-colors"
                                style={{
                                    background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6',
                                    color: isDark ? '#cbd5e1' : '#6b7280'
                                }}
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Filtros y búsqueda */}
                        <div className="flex flex-col md:flex-row gap-4 mb-6">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Buscar por código, remitente o descripción..."
                                    value={busqueda}
                                    onChange={(e) => setBusqueda(e.target.value)}
                                    className="pl-10 pr-4 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#61257B]/30 focus:border-[#61257B] w-full text-sm transition-all"
                                    style={{
                                        background: isDark ? 'rgba(15, 7, 28, 0.85)' : '#ffffff',
                                        color: isDark ? '#ffffff' : '#1f2937',
                                        border: isDark ? '1px solid rgba(193, 52, 139, 0.35)' : '1px solid #d1d5db'
                                    }}
                                />
                            </div>
                            <div className="flex gap-2">
                                <select
                                    value={filtroEstado}
                                    onChange={(e) => setFiltroEstado(e.target.value)}
                                    className="px-3.5 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#61257B]/30 focus:border-[#61257B] text-sm transition-all"
                                    style={{
                                        background: isDark ? 'rgba(15, 7, 28, 0.85)' : '#ffffff',
                                        color: isDark ? '#ffffff' : '#1f2937',
                                        border: isDark ? '1px solid rgba(193, 52, 139, 0.35)' : '1px solid #d1d5db'
                                    }}
                                >
                                    <option value="todos">Todos los estados</option>
                                    <option value="recibido">En Recepción</option>
                                    <option value="notificado">Listo para Retiro</option>
                                    <option value="entregado">Entregado</option>
                                    <option value="cancelado">Cancelado</option>
                                </select>
                            </div>
                        </div>

                        {isLoading ? (
                            <div className="text-center py-16">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#61257B] mx-auto"></div>
                                <p className="mt-4 text-sm" style={{ color: isDark ? '#cbd5e1' : '#4b5563' }}>Cargando correspondencias...</p>
                            </div>
                        ) : correspondenciasFiltradas.length > 0 ? (
                            <div className="space-y-4">
                                {correspondenciasFiltradas.map((correspondencia) => (
                                    <div
                                        key={correspondencia.id}
                                        className="bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-md transition-all duration-200"
                                    >
                                        <div className="flex flex-col lg:flex-row gap-5">
                                            {/* Foto de respaldo / Miniatura */}
                                            <div className="flex-shrink-0">
                                                {(correspondencia.imagen || (correspondencia as any).foto_paquete_url) ? (
                                                    <div
                                                        className="relative w-full lg:w-32 h-32 rounded-xl overflow-hidden bg-gray-100 cursor-pointer group border border-gray-200"
                                                        onClick={() => setVerFotoModal(correspondencia.imagen || (correspondencia as any).foto_paquete_url || null)}
                                                    >
                                                        <img
                                                            src={correspondencia.imagen || (correspondencia as any).foto_paquete_url}
                                                            alt="Foto Paquete"
                                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                                        />
                                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                                                            <Eye className="w-5 h-5" />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div 
                                                        className="w-24 h-24 rounded-xl flex flex-col items-center justify-center"
                                                        style={{
                                                            background: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6',
                                                            color: isDark ? '#94a3b8' : '#9ca3af'
                                                        }}
                                                    >
                                                        <ImageIcon className="w-8 h-8 mb-1" />
                                                        <span className="text-[10px]">Sin foto</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Detalles */}
                                            <div className="flex-1 space-y-3">
                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                    <div className="flex items-center gap-2">
                                                        <span 
                                                            className="px-3 py-1 text-sm font-mono font-bold rounded-lg border flex items-center gap-1.5"
                                                            style={{
                                                                background: isDark ? 'rgba(97, 37, 123, 0.25)' : 'rgba(97, 37, 123, 0.08)',
                                                                color: isDark ? '#ffd97c' : '#61257B',
                                                                borderColor: isDark ? 'rgba(193, 52, 139, 0.35)' : 'rgba(97, 37, 123, 0.25)'
                                                            }}
                                                        >
                                                            {correspondencia.codigo || `#${correspondencia.id}`}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleCopiarCodigo(correspondencia.id, correspondencia.codigo || `#${correspondencia.id}`)}
                                                                className="p-1 rounded transition-colors"
                                                                style={{ color: isDark ? '#ffd97c' : '#61257B' }}
                                                                title="Copiar código"
                                                            >
                                                                {copiadoCodigoId === correspondencia.id ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                                                            </button>
                                                        </span>
                                                        <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${getEstadoColor(correspondencia.estado)}`}>
                                                            {getEstadoTexto(correspondencia.estado)}
                                                        </span>
                                                    </div>

                                                    {/* Botón QR Comprobante para retiro */}
                                                    {correspondencia.estado !== 'cancelado' && (
                                                        <button
                                                            onClick={() => setComprobanteQR(correspondencia)}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-[#61257B] to-[#C1348B] hover:opacity-95 shadow-md rounded-xl transition-all"
                                                        >
                                                            <QrCode className="w-4 h-4" />
                                                            Comprobante / QR
                                                        </button>
                                                    )}
                                                </div>

                                                <p className="text-sm font-medium" style={{ color: isDark ? '#ffffff' : '#1f2937' }}>
                                                    {correspondencia.descripcion || 'Sin descripción especificada'}
                                                </p>

                                                <div 
                                                    className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 text-xs border-t"
                                                    style={{ borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6' }}
                                                >
                                                    <div>
                                                        <span className="block mb-0.5" style={{ color: isDark ? '#cbd5e1' : '#9ca3af' }}>Remitente:</span>
                                                        <p className="font-semibold" style={{ color: isDark ? '#ffffff' : '#374151' }}>{correspondencia.remitente_nombre} {correspondencia.remitente_apellido}</p>
                                                        <p style={{ color: isDark ? '#94a3b8' : '#6b7280' }}>{correspondencia.remitente_ci_nit}</p>
                                                    </div>

                                                    <div>
                                                        <span className="block mb-0.5" style={{ color: isDark ? '#cbd5e1' : '#9ca3af' }}>Fecha de Recepción:</span>
                                                        <p className="font-semibold" style={{ color: isDark ? '#ffffff' : '#374151' }}>{formatDateTime(correspondencia.fecha_recepcion)}</p>
                                                    </div>

                                                    <div>
                                                        <span className="block mb-0.5" style={{ color: isDark ? '#cbd5e1' : '#9ca3af' }}>Estado de Entrega:</span>
                                                        {correspondencia.estado === 'entregado' ? (
                                                            <div>
                                                                <p className="font-semibold text-emerald-500 flex items-center gap-1">
                                                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                                                    Retirado por: {correspondencia.receptor_nombre || 'Cliente'}
                                                                </p>
                                                                <p style={{ color: isDark ? '#94a3b8' : '#6b7280' }}>{formatDateTime(correspondencia.fecha_entrega)}</p>
                                                            </div>
                                                        ) : (
                                                            <p className="font-medium text-amber-500">Pendiente de Retiro</p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* PIN de Seguridad para Retiro (si está pendiente) */}
                                                {correspondencia.pin_seguridad && correspondencia.estado !== 'entregado' && correspondencia.estado !== 'cancelado' && (
                                                    <div 
                                                        className="mt-3 p-3 rounded-xl flex items-center justify-between"
                                                        style={{
                                                            background: isDark ? 'linear-gradient(135deg, rgba(97,37,123,0.2), rgba(193,52,139,0.15))' : 'linear-gradient(135deg, rgba(97,37,123,0.05), rgba(193,52,139,0.05))',
                                                            border: isDark ? '1px solid rgba(193, 52, 139, 0.3)' : '1px solid rgba(97, 37, 123, 0.2)'
                                                        }}
                                                    >
                                                        <div className="flex items-center gap-2.5">
                                                            <div 
                                                                className="p-1.5 rounded-lg"
                                                                style={{ background: 'rgba(97, 37, 123, 0.15)', color: isDark ? '#ffd97c' : '#61257B' }}
                                                            >
                                                                <Shield className="w-4 h-4" />
                                                            </div>
                                                            <div>
                                                                <span className="text-[11px] font-medium block" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>
                                                                    Tu PIN Secreto para Retirar este Paquete:
                                                                </span>
                                                                <span 
                                                                    className="text-lg font-mono font-extrabold tracking-widest"
                                                                    style={{ color: isDark ? '#ffd97c' : '#61257B' }}
                                                                >
                                                                    {correspondencia.pin_seguridad}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleCopiarPin(correspondencia.id, correspondencia.pin_seguridad || '')}
                                                            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg shadow-sm transition-colors"
                                                            style={{
                                                                background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#ffffff',
                                                                color: isDark ? '#ffffff' : '#61257B',
                                                                border: isDark ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(97, 37, 123, 0.25)'
                                                            }}
                                                        >
                                                            {copiadoPinId === correspondencia.id ? (
                                                                <>
                                                                    <Check className="w-3.5 h-3.5 text-green-500" />
                                                                    <span>¡Copiado!</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Copy className="w-3.5 h-3.5" />
                                                                    <span>Copiar PIN</span>
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-16">
                                <div 
                                    className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
                                    style={{ background: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6' }}
                                >
                                    <X className="h-8 w-8" style={{ color: isDark ? '#94a3b8' : '#9ca3af' }} />
                                </div>
                                <h3 className="text-base font-semibold mb-1" style={{ color: isDark ? '#ffffff' : '#1f2937' }}>
                                    No hay correspondencias registradas
                                </h3>
                                <p className="text-sm max-w-md mx-auto" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>
                                    {busqueda || filtroEstado !== 'todos'
                                        ? 'No se encontraron paquetes con los filtros aplicados'
                                        : 'Esta cita no tiene correspondencias registradas actualmente'
                                    }
                                </p>
                            </div>
                        )}

                        <div 
                            className="flex justify-between items-center pt-6 border-t mt-6 text-xs"
                            style={{ borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e5e7eb', color: isDark ? '#cbd5e1' : '#6b7280' }}
                        >
                            <div>
                                Mostrando {correspondenciasFiltradas.length} de {correspondencias.length} paquetes
                            </div>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium rounded-xl transition-colors"
                                style={{
                                    background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6',
                                    color: isDark ? '#ffffff' : '#374151',
                                    border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : 'none'
                                }}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default HistorialCorrespondenciaUsuarios;